/**
 * PAREERO HEALTH & FITNESS PLANNER - Core Local Data Structures
 * Strictly standalone: No external APIs, No CDNs, No remote dependencies.
 * Fully compatible with Shopify and offline browser execution.
 */

const PAREERO_DATA = {
  version: "1.0.0",
  appName: "Pareero Health & Fitness Planner",
  disclaimer: "This tool provides general educational estimates and is not medical advice, diagnosis or treatment. Calorie and exercise needs vary between individuals. Consult a qualified healthcare professional for personalized advice, particularly if you are pregnant, breastfeeding, under 18, have a medical condition, take medications, have a history of an eating disorder, or have significant unexplained weight changes.",
  
  // Theme Presets for Customization
  themes: {
    palettes: [
      {
        id: "heritage-gold",
        name: "Pareero Signature Gold & Espresso",
        primary: "#C89B3C",
        primaryHover: "#B2852B",
        primaryLight: "#FBF5E8",
        primaryBorder: "#E8D39E",
        dark: "#241A15",
        darkMuted: "#42332A",
        bgLight: "#FAF7F2",
        cardBg: "#FFFFFF",
        badgeBg: "#F5EDE0"
      },
      {
        id: "emerald-sage",
        name: "Emerald Sage & Forest Moss",
        primary: "#337A59",
        primaryHover: "#286246",
        primaryLight: "#EEF6F2",
        primaryBorder: "#A8CEBD",
        dark: "#14231B",
        darkMuted: "#2B4034",
        bgLight: "#F3F7F5",
        cardBg: "#FFFFFF",
        badgeBg: "#E3ECE6"
      },
      {
        id: "desert-terracotta",
        name: "Desert Rose & Rich Terracotta",
        primary: "#BD5C42",
        primaryHover: "#A54D35",
        primaryLight: "#FAF0EC",
        primaryBorder: "#ECC3B7",
        dark: "#291814",
        darkMuted: "#4A3029",
        bgLight: "#FAF5F3",
        cardBg: "#FFFFFF",
        badgeBg: "#F4E5E0"
      },
      {
        id: "ocean-slate",
        name: "Aegean Slate & Deep Obsidian",
        primary: "#317496",
        primaryHover: "#265C78",
        primaryLight: "#EDF5F9",
        primaryBorder: "#A7CEDB",
        dark: "#111C24",
        darkMuted: "#273845",
        bgLight: "#F2F6F9",
        cardBg: "#FFFFFF",
        badgeBg: "#E1ECF2"
      },
      {
        id: "royal-bronze",
        name: "Olive Bronze & Smoked Cocoa",
        primary: "#8C7335",
        primaryHover: "#735E2A",
        primaryLight: "#F8F5EB",
        primaryBorder: "#D3C6A2",
        dark: "#1E1A14",
        darkMuted: "#3D352B",
        bgLight: "#F7F5F0",
        cardBg: "#FFFFFF",
        badgeBg: "#EFEADF"
      }
    ],
    fonts: [
      {
        id: "modern-sans",
        name: "Pareero Modern Sans (Clean & Crisp)",
        family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      },
      {
        id: "editorial-serif",
        name: "Editorial Heritage (Refined Serif)",
        family: 'Georgia, Cambria, "Times New Roman", Times, serif'
      },
      {
        id: "geometric-clean",
        name: "Geometric Luxury (Modern Minimal)",
        family: 'system-ui, -apple-system, "Segoe UI", "Avenir Next", "Century Gothic", sans-serif'
      },
      {
        id: "studio-mono",
        name: "Technical Precision (Refined Monospace)",
        family: 'ui-monospace, "SF Mono", "Cascadia Code", "Roboto Mono", Menlo, Consolas, monospace'
      }
    ],
    moods: [
      { id: "mood-cream", name: "Warm Cream & Linen (Signature)" },
      { id: "mood-pure-white", name: "Crisp Pure White" },
      { id: "mood-charcoal", name: "Deep Luxury Charcoal (Dark Mode)" }
    ]
  },

  // Daily Habits for the Wellness tracker
  habits: [
    { id: "h1", title: "Target Hydration", desc: "Drink at least 2.5–3.0 liters of pure water", icon: "water" },
    { id: "h2", title: "Active Steps", desc: "Achieve 7,500+ brisk daily steps", icon: "footsteps" },
    { id: "h3", title: "Strength or Active Session", desc: "Complete 20–45 mins of focused workout or mobility", icon: "activity" },
    { id: "h4", title: "Fresh Veggies & Fruits", desc: "Include at least 2 colorful portions (kachumber, sabzi, apple)", icon: "leaf" },
    { id: "h5", title: "Hit Daily Protein Goal", desc: "Distribute lean meats, eggs, lentils, or dahi across meals", icon: "shield" },
    { id: "h6", title: "Restorative Sleep Routine", desc: "7 to 8 hours in a quiet, dark, tech-free environment", icon: "moon" },
    { id: "h7", title: "Limit Sugary Sodas & Chai Sugar", desc: "Opt for kahwa, unsweetened chai, or plain water", icon: "heart" },
    { id: "h8", title: "Standing & Movement Breaks", desc: "Stand and stretch every 60–90 minutes of seated work", icon: "refresh" }
  ],

  // Comprehensive Pakistani & Healthy Food Database
  // All nutritional values are realistic estimations based on standardized South Asian food composition tables
  foods: [
    // BREADS & GRAINS
    { id: "f01", name: "Roti / Chapati (Whole Wheat)", urdu: "گندم کی روٹی", category: "Breads & Grains", serving: "1 medium (60g)", calories: 150, protein: 4.8, carbs: 30.2, fat: 1.1, fiber: 3.6 },
    { id: "f02", name: "Tawa Paratha (with Ghee)", urdu: "پراٹھا", category: "Breads & Grains", serving: "1 standard (80g)", calories: 290, protein: 4.5, carbs: 34.0, fat: 15.0, fiber: 2.1 },
    { id: "f03", name: "Aloo Paratha", urdu: "آلو والا پراٹھا", category: "Breads & Grains", serving: "1 stuffed (120g)", calories: 340, protein: 6.2, carbs: 48.0, fat: 14.2, fiber: 3.5 },
    { id: "f04", name: "Tandoori Plain Naan", urdu: "تندوری نان", category: "Breads & Grains", serving: "1 piece (120g)", calories: 310, protein: 9.1, carbs: 58.0, fat: 4.2, fiber: 2.2 },
    { id: "f05", name: "Roghani Naan (Sesame & Ghee)", urdu: "روغنی نان", category: "Breads & Grains", serving: "1 piece (150g)", calories: 410, protein: 11.2, carbs: 68.0, fat: 9.5, fiber: 2.4 },
    { id: "f06", name: "Basmati Boiled White Rice", urdu: "ابلے چاول", category: "Breads & Grains", serving: "1 cup cooked (160g)", calories: 210, protein: 4.3, carbs: 45.2, fat: 0.5, fiber: 1.0 },
    { id: "f07", name: "Brown Rice (Cooked)", urdu: "براؤن رائس", category: "Breads & Grains", serving: "1 cup cooked (195g)", calories: 216, protein: 5.0, carbs: 44.8, fat: 1.8, fiber: 3.5 },
    { id: "f08", name: "Chicken Biryani", urdu: "چکن بریانی", category: "Breads & Grains", serving: "1 standard plate (300g)", calories: 480, protein: 28.5, carbs: 58.0, fat: 15.2, fiber: 3.1 },
    { id: "f09", name: "Mutton Biryani", urdu: "مٹن بریانی", category: "Breads & Grains", serving: "1 plate (300g)", calories: 590, protein: 32.0, carbs: 56.0, fat: 26.5, fiber: 2.8 },
    { id: "f10", name: "Chicken Pulao", urdu: "چکن پلاؤ", category: "Breads & Grains", serving: "1 plate (280g)", calories: 420, protein: 24.2, carbs: 52.0, fat: 13.5, fiber: 2.5 },
    { id: "f11", name: "Matar Pulao (Peas Rice)", urdu: "مٹر پلاؤ", category: "Breads & Grains", serving: "1 plate (250g)", calories: 310, protein: 7.5, carbs: 53.0, fat: 7.8, fiber: 4.2 },

    // POULTRY, MEATS & SEAFOOD
    { id: "f12", name: "Chicken Karahi (Home Style)", urdu: "چکن کڑاہی", category: "Meats & Poultry", serving: "1 serving (200g)", calories: 340, protein: 34.0, carbs: 6.5, fat: 20.0, fiber: 1.8 },
    { id: "f13", name: "Chicken Tikka (Breast Quarter Grilled)", urdu: "چکن تکہ", category: "Meats & Poultry", serving: "1 piece (180g)", calories: 260, protein: 38.5, carbs: 2.5, fat: 10.5, fiber: 0.8 },
    { id: "f14", name: "Chicken Seekh Kabab", urdu: "چکن سیخ کباب", category: "Meats & Poultry", serving: "2 skewers (120g)", calories: 230, protein: 26.0, carbs: 4.5, fat: 12.0, fiber: 1.2 },
    { id: "f15", name: "Beef Seekh Kabab", urdu: "بیف سیخ کباب", category: "Meats & Poultry", serving: "2 skewers (130g)", calories: 310, protein: 27.2, carbs: 4.0, fat: 21.0, fiber: 1.0 },
    { id: "f16", name: "Shami Kabab (Beef & Lentil)", urdu: "شامی کباب", category: "Meats & Poultry", serving: "1 patty (65g)", calories: 145, protein: 11.5, carbs: 9.0, fat: 6.8, fiber: 2.2 },
    { id: "f17", name: "Chicken Shami Kabab", urdu: "چکن شامی کباب", category: "Meats & Poultry", serving: "1 patty (65g)", calories: 125, protein: 13.0, carbs: 8.0, fat: 4.5, fiber: 2.0 },
    { id: "f18", name: "Beef Nihari (Light Gravy)", urdu: "بیف نہاری", category: "Meats & Poultry", serving: "1 bowl (250g)", calories: 480, protein: 35.0, carbs: 14.0, fat: 31.0, fiber: 1.8 },
    { id: "f19", name: "Mutton Karahi", urdu: "مٹن کڑاہی", category: "Meats & Poultry", serving: "1 serving (200g)", calories: 460, protein: 31.0, carbs: 5.5, fat: 34.0, fiber: 1.5 },
    { id: "f20", name: "Mutton Paya Broth", urdu: "مٹن پائے", category: "Meats & Poultry", serving: "1 bowl (300g)", calories: 380, protein: 28.0, carbs: 5.0, fat: 27.0, fiber: 0.5 },
    { id: "f21", name: "Haleem (Beef & Multi-Lentil)", urdu: "حلیم", category: "Meats & Poultry", serving: "1 bowl (300g)", calories: 430, protein: 32.5, carbs: 46.0, fat: 13.0, fiber: 8.5 },
    { id: "f22", name: "Skinless Boiled/Grilled Chicken Breast", urdu: "ابلا ہوا چکن بریسٹ", category: "Meats & Poultry", serving: "1 fillet (150g)", calories: 245, protein: 46.0, carbs: 0.0, fat: 5.2, fiber: 0.0 },
    { id: "f23", name: "Pan-Seared Fish Fillet (Rahu/Pomfret)", urdu: "گرلڈ مچھلی", category: "Meats & Poultry", serving: "1 fillet (160g)", calories: 215, protein: 32.0, carbs: 1.0, fat: 9.0, fiber: 0.0 },
    { id: "f24", name: "Lahori Spiced Fried Fish", urdu: "لاہوری تلی مچھلی", category: "Meats & Poultry", serving: "1 fillet (180g)", calories: 340, protein: 28.0, carbs: 11.0, fat: 19.5, fiber: 1.0 },

    // EGGS & DAIRY
    { id: "f25", name: "Hard Boiled Egg", urdu: "ابلا ہوا انڈا", category: "Eggs & Dairy", serving: "1 large (50g)", calories: 74, protein: 6.3, carbs: 0.4, fat: 5.0, fiber: 0.0 },
    { id: "f26", name: "Fried Egg (1 tsp oil)", urdu: "تلا ہوا انڈا", category: "Eggs & Dairy", serving: "1 large (55g)", calories: 115, protein: 6.3, carbs: 0.4, fat: 9.8, fiber: 0.0 },
    { id: "f27", name: "Desi Omelette (with onion & chili)", urdu: "دیسی آملیٹ", category: "Eggs & Dairy", serving: "1 large egg portion (70g)", calories: 135, protein: 7.2, carbs: 3.2, fat: 10.4, fiber: 0.8 },
    { id: "f28", name: "Egg Whites (Boiled)", urdu: "انڈے کی سفیدی", category: "Eggs & Dairy", serving: "3 whites (100g)", calories: 52, protein: 11.0, carbs: 0.7, fat: 0.2, fiber: 0.0 },
    { id: "f29", name: "Dahi (Plain Whole Milk Yogurt)", urdu: "دہی", category: "Eggs & Dairy", serving: "1 cup (200g)", calories: 140, protein: 7.0, carbs: 9.5, fat: 8.0, fiber: 0.0 },
    { id: "f30", name: "Dahi (Low Fat / Skim Yogurt)", urdu: "لو فیٹ دہی", category: "Eggs & Dairy", serving: "1 cup (200g)", calories: 95, protein: 9.2, carbs: 12.0, fat: 1.5, fiber: 0.0 },
    { id: "f31", name: "Buffalo Full-Cream Milk", urdu: "بھینس کا دودھ", category: "Eggs & Dairy", serving: "1 glass (250ml)", calories: 185, protein: 9.2, carbs: 13.0, fat: 11.0, fiber: 0.0 },
    { id: "f32", name: "Cow Milk (Low Fat 1.5%)", urdu: "گائے کا ہلکا دودھ", category: "Eggs & Dairy", serving: "1 glass (250ml)", calories: 118, protein: 8.5, carbs: 12.2, fat: 3.8, fiber: 0.0 },
    { id: "f33", name: "Paneer / Cottage Cheese", urdu: "پنیر", category: "Eggs & Dairy", serving: "100g", calories: 265, protein: 18.5, carbs: 3.5, fat: 20.0, fiber: 0.0 },

    // LENTILS, DAALS & BEANS
    { id: "f34", name: "Daal Chana (Yellow Split Chickpeas)", urdu: "دال چنا", category: "Daals & Beans", serving: "1 cup cooked (200g)", calories: 240, protein: 13.2, carbs: 35.0, fat: 5.5, fiber: 8.2 },
    { id: "f35", name: "Daal Maash / Urad (Tarka)", urdu: "دال ماش", category: "Daals & Beans", serving: "1 cup cooked (200g)", calories: 230, protein: 14.0, carbs: 33.0, fat: 5.0, fiber: 7.5 },
    { id: "f36", name: "Daal Masoor / Moong Tadka", urdu: "دال مسور / مونگ", category: "Daals & Beans", serving: "1 cup cooked (200g)", calories: 190, protein: 12.0, carbs: 29.5, fat: 4.2, fiber: 6.2 },
    { id: "f37", name: "Chana Masala (White Chickpeas)", urdu: "سفید چنے", category: "Daals & Beans", serving: "1 cup cooked (200g)", calories: 260, protein: 12.5, carbs: 38.0, fat: 6.2, fiber: 9.5 },
    { id: "f38", name: "Rajma / Lobia (Red Kidney / Cowpeas)", urdu: "راجما / لوبیا", category: "Daals & Beans", serving: "1 cup cooked (200g)", calories: 220, protein: 13.5, carbs: 36.0, fat: 3.2, fiber: 9.0 },

    // VEGETABLES & SABZI
    { id: "f39", name: "Aloo Palak (Potato Spinach)", urdu: "آلو پالک", category: "Vegetables & Sabzi", serving: "1 cup (200g)", calories: 175, protein: 4.5, carbs: 24.0, fat: 7.2, fiber: 5.1 },
    { id: "f40", name: "Palak Paneer", urdu: "پالک پنیر", category: "Vegetables & Sabzi", serving: "1 cup (200g)", calories: 270, protein: 14.0, carbs: 10.0, fat: 20.0, fiber: 4.5 },
    { id: "f41", name: "Sarson Ka Saag (with Makhan hint)", urdu: "سرسوں کا ساگ", category: "Vegetables & Sabzi", serving: "1 cup (200g)", calories: 210, protein: 6.2, carbs: 14.0, fat: 15.0, fiber: 6.5 },
    { id: "f42", name: "Aloo Gobi Masala", urdu: "آلو گوبھی", category: "Vegetables & Sabzi", serving: "1 cup (200g)", calories: 160, protein: 4.0, carbs: 22.5, fat: 6.5, fiber: 4.2 },
    { id: "f43", name: "Bhindi Masala (Stir-fried Okra)", urdu: "بھنڈی مصالحہ", category: "Vegetables & Sabzi", serving: "1 cup (180g)", calories: 150, protein: 3.5, carbs: 16.0, fat: 8.0, fiber: 5.2 },
    { id: "f44", name: "Kachumber Fresh Salad (Cucumber, Tomato, Onion)", urdu: "کچومر سلاد", category: "Vegetables & Sabzi", serving: "1 bowl (150g)", calories: 35, protein: 1.5, carbs: 7.0, fat: 0.3, fiber: 2.5 },

    // BEVERAGES
    { id: "f45", name: "Desi Doodh Patti Chai (with 1 tsp sugar)", urdu: "دودھ پتی چائے", category: "Beverages", serving: "1 standard cup (180ml)", calories: 145, protein: 4.5, carbs: 16.0, fat: 7.0, fiber: 0.0 },
    { id: "f46", name: "Chai (Unsweetened with Low Fat Milk)", urdu: "بغیر چینی چائے", category: "Beverages", serving: "1 cup (180ml)", calories: 65, protein: 3.5, carbs: 5.0, fat: 3.0, fiber: 0.0 },
    { id: "f47", name: "Peshawari Kahwa / Green Tea", urdu: "قہوہ / سبز چائے", category: "Beverages", serving: "1 cup (200ml)", calories: 3, protein: 0.2, carbs: 0.5, fat: 0.0, fiber: 0.0 },
    { id: "f48", name: "Sweet Lassi (Dahi & Sugar)", urdu: "میٹھی لسی", category: "Beverages", serving: "1 tall glass (300ml)", calories: 280, protein: 7.0, carbs: 46.0, fat: 7.5, fiber: 0.0 },
    { id: "f49", name: "Namkeen / Salted Cumin Lassi", urdu: "نمکین لسی", category: "Beverages", serving: "1 tall glass (300ml)", calories: 130, protein: 7.0, carbs: 10.0, fat: 7.0, fiber: 0.0 },

    // FRUITS, NUTS & SEEDS
    { id: "f50", name: "Banana", urdu: "کیلا", category: "Fruits & Nuts", serving: "1 medium (118g)", calories: 105, protein: 1.3, carbs: 27.0, fat: 0.3, fiber: 3.1 },
    { id: "f51", name: "Fresh Apple", urdu: "سیب", category: "Fruits & Nuts", serving: "1 medium (182g)", calories: 95, protein: 0.5, carbs: 25.0, fat: 0.3, fiber: 4.4 },
    { id: "f52", name: "Pakistani Mango (Chaunsa / Sindhri)", urdu: "آم (چونسا)", category: "Fruits & Nuts", serving: "1 medium (200g pulp)", calories: 150, protein: 1.6, carbs: 38.0, fat: 0.8, fiber: 3.2 },
    { id: "f53", name: "Aseel / Medjool Dates (Khajoor)", urdu: "کھجور", category: "Fruits & Nuts", serving: "3 dates (45g)", calories: 125, protein: 1.0, carbs: 33.0, fat: 0.2, fiber: 3.0 },
    { id: "f54", name: "Almonds / Badam", urdu: "بادام", category: "Fruits & Nuts", serving: "1 ounce (28g / ~23 nuts)", calories: 164, protein: 6.0, carbs: 6.0, fat: 14.0, fiber: 3.5 },
    { id: "f55", name: "Walnuts / Akhrot", urdu: "اخروٹ", category: "Fruits & Nuts", serving: "1 ounce (28g / ~14 halves)", calories: 185, protein: 4.3, carbs: 3.9, fat: 18.5, fiber: 1.9 },
    { id: "f56", name: "Roasted Peanuts (Moongphali)", urdu: "مونگ پھلی", category: "Fruits & Nuts", serving: "1 ounce (28g)", calories: 166, protein: 7.3, carbs: 4.6, fat: 14.0, fiber: 2.4 },

    // SNACKS & TREATS (PORTION AWARENESS)
    { id: "f57", name: "Vegetable Samosa", urdu: "آلو سموسہ", category: "Snacks & Sweets", serving: "1 piece (80g)", calories: 220, protein: 4.0, carbs: 27.0, fat: 11.0, fiber: 2.5 },
    { id: "f58", name: "Chicken Keema Samosa", urdu: "قیمہ سموسہ", category: "Snacks & Sweets", serving: "1 piece (80g)", calories: 240, protein: 10.0, carbs: 22.0, fat: 12.5, fiber: 1.5 },
    { id: "f59", name: "Mixed Vegetable Pakora", urdu: "پکوڑے", category: "Snacks & Sweets", serving: "4-5 pieces (100g)", calories: 280, protein: 6.0, carbs: 26.0, fat: 17.0, fiber: 3.0 },
    { id: "f60", name: "Gulab Jamun (with sugar syrup)", urdu: "گلاب جامن", category: "Snacks & Sweets", serving: "1 piece (50g)", calories: 175, protein: 2.5, carbs: 26.0, fat: 7.0, fiber: 0.2 },
    { id: "f61", name: "Jalebi", urdu: "جلیبی", category: "Snacks & Sweets", serving: "3 pieces (100g)", calories: 380, protein: 2.5, carbs: 78.0, fat: 7.0, fiber: 0.5 },
    { id: "f62", name: "Kheer (Cardamom Rice Pudding)", urdu: "کھیر", category: "Snacks & Sweets", serving: "1 small bowl (150g)", calories: 210, protein: 5.5, carbs: 32.0, fat: 7.5, fiber: 0.5 }
  ],

  // Comprehensive Exercise Library with Detailed Form Cues, Muscle Groups & Modifications
  exercises: [
    // CHEST
    {
      id: "ex01",
      name: "Barbell Bench Press",
      category: "Chest",
      equipment: "Full Gym",
      primaryMuscles: "Pectoralis Major, Anterior Deltoids, Triceps",
      difficulty: "Intermediate",
      sets: "3–4",
      reps: "6–10",
      rest: "90–120s",
      cue: "Retract scapulae into bench, arch slightly, drive feet into floor, lower bar under control to mid-chest, press up in a slight arc.",
      beginnerMod: "Dumbbell Flat Press or Push-ups from knees",
      progression: "Add 1.25–2.5kg per session or add 1–2 reps with strict pause at bottom"
    },
    {
      id: "ex02",
      name: "Dumbbell Incline Bench Press",
      category: "Chest",
      equipment: "Dumbbells",
      primaryMuscles: "Clavicular Pectoralis (Upper Chest), Triceps",
      difficulty: "Intermediate",
      sets: "3–4",
      reps: "8–12",
      rest: "90s",
      cue: "Bench set at 30 degrees. Keep wrists stacked over elbows. Squeeze chest at the top without clanking dumbbells together.",
      beginnerMod: "Lower dumbbell weight with tempo (3s eccentric)",
      progression: "Increase load when hitting 12 reps on all sets"
    },
    {
      id: "ex03",
      name: "Push-ups (Standard Bodyweight)",
      category: "Chest",
      equipment: "Bodyweight",
      primaryMuscles: "Chest, Core, Triceps, Shoulders",
      difficulty: "Beginner",
      sets: "3",
      reps: "8–15",
      rest: "60s",
      cue: "Hands slightly wider than shoulders. Form a straight line from heels to crown. Tuck elbows at ~45-degree angle.",
      beginnerMod: "Incline Push-ups against a sturdy counter or bench, or knee push-ups",
      progression: "Decline push-ups with feet elevated or weighted vest"
    },
    {
      id: "ex04",
      name: "Dumbbell Floor Press",
      category: "Chest",
      equipment: "Dumbbells",
      primaryMuscles: "Chest, Triceps",
      difficulty: "Beginner",
      sets: "3",
      reps: "10–12",
      rest: "60–90s",
      cue: "Lie on floor with knees bent. Lower dumbbells until upper triceps gently kiss the floor, pause briefly, then press smoothly up.",
      beginnerMod: "Lighter dumbbells or single-arm floor press with core braced",
      progression: "3-second slow descent on every repetition"
    },

    // BACK
    {
      id: "ex05",
      name: "Bent-Over Barbell Row",
      category: "Back",
      equipment: "Full Gym",
      primaryMuscles: "Latissimus Dorsi, Rhomboids, Trapezius, Posterior Delts",
      difficulty: "Intermediate",
      sets: "3–4",
      reps: "6–10",
      rest: "90s",
      cue: "Hinge at hips to 45 degrees, spine neutral. Pull bar to upper abdomen driving through elbows, squeeze shoulder blades together.",
      beginnerMod: "Chest-supported Dumbbell Row on incline bench",
      progression: "Increase barbell load or pause 1 second at full contraction"
    },
    {
      id: "ex06",
      name: "One-Arm Dumbbell Row",
      category: "Back",
      equipment: "Dumbbells",
      primaryMuscles: "Lats, Upper Back, Biceps",
      difficulty: "Beginner",
      sets: "3",
      reps: "8–12 per side",
      rest: "60s",
      cue: "Brace on a bench or chair. Pull dumbbell toward hip pocket keeping elbow tucked close to ribcage.",
      beginnerMod: "Use a chair for secure two-point support",
      progression: "Increase dumbbell weight or eliminate torso rotation"
    },
    {
      id: "ex07",
      name: "Pull-ups / Chin-ups",
      category: "Back",
      equipment: "Bodyweight",
      primaryMuscles: "Lats, Biceps, Forearms, Core",
      difficulty: "Advanced",
      sets: "3–4",
      reps: "4–8",
      rest: "120s",
      cue: "Hang with full shoulder flexion. Initiate pull by depressing shoulder blades, drive elbows down to ribs, chest to bar.",
      beginnerMod: "Resistance band-assisted pull-ups or negative-only pull-ups (5s descent)",
      progression: "Add weight belt or chest-to-bar pull-ups"
    },
    {
      id: "ex08",
      name: "Resistance Band Seated / Standing Row",
      category: "Back",
      equipment: "Resistance Bands",
      primaryMuscles: "Middle Back, Rhomboids, Rear Delts",
      difficulty: "Beginner",
      sets: "3",
      reps: "12–15",
      rest: "60s",
      cue: "Anchor band securely or loop around feet. Keep chest tall, pull handles to sides while squeezing upper back.",
      beginnerMod: "Lighter resistance band or stand closer to anchor",
      progression: "Use heavier band with 2-second squeeze at peak contraction"
    },

    // SHOULDERS
    {
      id: "ex09",
      name: "Overhead Barbell / Dumbbell Press",
      category: "Shoulders",
      equipment: "Full Gym",
      primaryMuscles: "Anterior & Lateral Deltoids, Triceps, Upper Trapezius",
      difficulty: "Intermediate",
      sets: "3–4",
      reps: "6–10",
      rest: "90s",
      cue: "Stand tall with glutes and abs squeezed tight. Press bar vertically overhead, clearing forehead, lock out with biceps by ears.",
      beginnerMod: "Seated Dumbbell Shoulder Press with back support",
      progression: "Progressive weight increments or strict standing tempo"
    },
    {
      id: "ex10",
      name: "Dumbbell Lateral Raises",
      category: "Shoulders",
      equipment: "Dumbbells",
      primaryMuscles: "Lateral Deltoids (Shoulder Width)",
      difficulty: "Beginner",
      sets: "3–4",
      reps: "12–15",
      rest: "60s",
      cue: "Slight forward torso lean. Raise dumbbells out to sides leading slightly with elbows until parallel with floor. Avoid swinging.",
      beginnerMod: "Use lighter weights (2–4kg) or resistance band",
      progression: "Pause for 1 second at horizontal level"
    },
    {
      id: "ex11",
      name: "Resistance Band Face Pulls",
      category: "Shoulders",
      equipment: "Resistance Bands",
      primaryMuscles: "Rear Delts, Rotator Cuff, Rhomboids",
      difficulty: "Beginner",
      sets: "3",
      reps: "15–20",
      rest: "60s",
      cue: "Anchor band at eye level. Pull toward bridge of nose while externally rotating hands so thumbs point backward.",
      beginnerMod: "Perform with lighter tension focus on postural alignment",
      progression: "Add 2-second hold at end range for upper back health"
    },

    // LEGS & GLUTES
    {
      id: "ex12",
      name: "Barbell Back / Front Squat",
      category: "Legs",
      equipment: "Full Gym",
      primaryMuscles: "Quadriceps, Gluteus Maximus, Adductors, Core",
      difficulty: "Intermediate",
      sets: "3–4",
      reps: "6–10",
      rest: "120s",
      cue: "Set feet shoulder-width, toes angled slightly out. Sit back and down between hips, keep chest proud, reach parallel depth, drive up through midfoot.",
      beginnerMod: "Goblet Squat with single dumbbell or Bodyweight Box Squat",
      progression: "Gradually add weight onto barbell"
    },
    {
      id: "ex13",
      name: "Dumbbell Goblet Squat",
      category: "Legs",
      equipment: "Dumbbells",
      primaryMuscles: "Quads, Glutes, Core",
      difficulty: "Beginner",
      sets: "3",
      reps: "10–12",
      rest: "75s",
      cue: "Hold dumbbell vertically against upper chest like a goblet. Keep elbows inside knees as you descend smoothly.",
      beginnerMod: "Bodyweight squat onto a standard chair",
      progression: "Heavier dumbbell or pause 2 seconds at bottom"
    },
    {
      id: "ex14",
      name: "Romanian Deadlift (RDL)",
      category: "Legs",
      equipment: "Dumbbells",
      primaryMuscles: "Hamstrings, Gluteus Maximus, Lower Back",
      difficulty: "Intermediate",
      sets: "3–4",
      reps: "8–12",
      rest: "90s",
      cue: "Soft bend in knees. Push hips straight back towards wall behind you, sliding weights down shins until deep hamstring stretch. Keep back flat.",
      beginnerMod: "Practice hip hinge with hands on hips against a wall",
      progression: "Heavier dumbbells or barbell with strict controlled tempo"
    },
    {
      id: "ex15",
      name: "Walking Lunges / Reverse Lunges",
      category: "Legs",
      equipment: "Bodyweight",
      primaryMuscles: "Quadriceps, Glutes, Hamstrings, Balance",
      difficulty: "Beginner",
      sets: "3",
      reps: "10–12 per leg",
      rest: "60s",
      cue: "Step backward (safer on knees) or forward. Lower rear knee toward floor to 90 degrees. Drive through front heel to return.",
      beginnerMod: "Stationary split squat holding a wall for balance",
      progression: "Hold dumbbells at sides or elevate front foot"
    },
    {
      id: "ex16",
      name: "Glute Bridges / Hip Thrusts",
      category: "Legs",
      equipment: "Bodyweight",
      primaryMuscles: "Gluteus Maximus, Hamstrings",
      difficulty: "Beginner",
      sets: "3",
      reps: "12–15",
      rest: "60s",
      cue: "Lie on back with knees bent, feet flat. Drive through heels to lift hips until hips, knees, and shoulders form straight line. Squeeze glutes at top.",
      beginnerMod: "Standard double-leg glute bridge on floor",
      progression: "Single-leg glute bridge or barbell hip thrust with back on bench"
    },
    {
      id: "ex17",
      name: "Standing Calf Raises",
      category: "Legs",
      equipment: "Bodyweight",
      primaryMuscles: "Gastrocnemius, Soleus",
      difficulty: "Beginner",
      sets: "3",
      reps: "15–20",
      rest: "45s",
      cue: "Stand on edge of step. Lower heels for a deep calf stretch, then rise onto balls of feet as high as possible. Pause at peak.",
      beginnerMod: "Flat floor calf raises near a wall",
      progression: "Single-leg calf raise holding a dumbbell"
    },

    // ARMS (BICEPS & TRICEPS)
    {
      id: "ex18",
      name: "Dumbbell Bicep Curls",
      category: "Arms",
      equipment: "Dumbbells",
      primaryMuscles: "Biceps Brachii, Brachialis",
      difficulty: "Beginner",
      sets: "3",
      reps: "10–12",
      rest: "60s",
      cue: "Keep upper arms pinned to sides. Curl weights upward, rotating palms facing upward (supinating), squeeze bicep at peak.",
      beginnerMod: "Lighter dumbbells or seated bicep curls",
      progression: "Hammer curls or incline bench curls"
    },
    {
      id: "ex19",
      name: "Overhead Dumbbell Tricep Extension",
      category: "Arms",
      equipment: "Dumbbells",
      primaryMuscles: "Triceps (Long Head)",
      difficulty: "Beginner",
      sets: "3",
      reps: "10–12",
      rest: "60s",
      cue: "Hold one dumbbell with both hands overhead. Lower weight behind head by bending elbows while keeping upper arms vertical.",
      beginnerMod: "Tricep bench dips with knees bent",
      progression: "Single arm extension or heavier dumbbell"
    },
    {
      id: "ex20",
      name: "Bench / Chair Dips",
      category: "Arms",
      equipment: "Bodyweight",
      primaryMuscles: "Triceps, Anterior Deltoids",
      difficulty: "Beginner",
      sets: "3",
      reps: "10–15",
      rest: "60s",
      cue: "Hands on sturdy edge behind you, fingers forward. Lower body by flexing elbows to ~90 degrees, press through palms to lockout.",
      beginnerMod: "Feet close with knees bent at 90 degrees",
      progression: "Extend legs straight or elevate feet on second chair"
    },

    // CORE & STABILITY
    {
      id: "ex21",
      name: "Forearm Plank",
      category: "Core",
      equipment: "Bodyweight",
      primaryMuscles: "Transverse Abdominis, Rectus Abdominis, Glutes",
      difficulty: "Beginner",
      sets: "3",
      reps: "30–60s hold",
      rest: "45s",
      cue: "Elbows under shoulders, forearms parallel. Squeeze glutes, brace abs as if expecting a gentle tap, breathe smoothly through nose.",
      beginnerMod: "Knee plank or elevated incline plank on bench",
      progression: "RKC active plank (actively pull elbows to toes) or plank with leg lifts"
    },
    {
      id: "ex22",
      name: "Dead Bug",
      category: "Core",
      equipment: "Bodyweight",
      primaryMuscles: "Deep Core, Pelvic Stability",
      difficulty: "Beginner",
      sets: "3",
      reps: "8–10 per side",
      rest: "45s",
      cue: "Lie on back, press lumbar spine flat against floor. Slowly extend opposite arm and leg while keeping core firmly braced.",
      beginnerMod: "Tap heel to floor with bent knees instead of extending leg straight",
      progression: "Add light weight to hands or resistance band around feet"
    },
    {
      id: "ex23",
      name: "Bird Dog",
      category: "Core",
      equipment: "Bodyweight",
      primaryMuscles: "Erector Spinae, Glutes, Shoulder Stabilizers",
      difficulty: "Beginner",
      sets: "3",
      reps: "10 per side",
      rest: "45s",
      cue: "On hands and knees. Reach opposite arm forward and leg straight back. Form straight line from fingers to heel without hip twisting.",
      beginnerMod: "Move arm only or leg only first",
      progression: "Hold for 3 seconds at top with square hips"
    },

    // CARDIO & CONDITIONING
    {
      id: "ex24",
      name: "Brisk Incline Walking / Outdoor Walk",
      category: "Conditioning",
      equipment: "None",
      primaryMuscles: "Cardiovascular System, Calves, Hamstrings",
      difficulty: "Beginner",
      sets: "1",
      reps: "20–45 mins",
      rest: "None",
      cue: "Maintain an upright posture, engage arms, walk at a pace where you can talk in sentences but cannot sing (Zone 2 cardio).",
      beginnerMod: "10–15 minutes easy walk split twice a day",
      progression: "Add incline (4–8%) or increase brisk pace"
    },
    {
      id: "ex25",
      name: "Bodyweight Jumping Jacks / Step Jacks",
      category: "Conditioning",
      equipment: "Bodyweight",
      primaryMuscles: "Full Body Cardio, Calves",
      difficulty: "Beginner",
      sets: "3",
      reps: "45s work / 15s rest",
      rest: "60s",
      cue: "Land softly on balls of feet, keep knees soft. Synchronize arms overhead with leg jump.",
      beginnerMod: "Step jacks without jumping (low impact on joints)",
      progression: "High knees or mountain climbers"
    }
  ],

  // Structured Workout Programs
  workoutPrograms: [
    {
      id: "prog-fullbody-3d",
      name: "3-Day Full Body Foundations",
      daysPerWeek: 3,
      duration: "40–50 mins",
      target: "All Fitness Levels / Fat Loss & Muscle Balance",
      location: "Gym or Home Dumbbell",
      desc: "An evidence-based routine training all major muscle groups 3 times weekly for maximum metabolic stimulation and strength balance.",
      days: [
        {
          name: "Day 1: Full Body Strength A",
          exercises: ["ex12", "ex01", "ex06", "ex14", "ex21", "ex24"]
        },
        {
          name: "Day 2: Full Body Hypertrophy B",
          exercises: ["ex13", "ex02", "ex05", "ex15", "ex10", "ex22"]
        },
        {
          name: "Day 3: Full Body Balance C",
          exercises: ["ex14", "ex03", "ex07", "ex16", "ex18", "ex23"]
        }
      ]
    },
    {
      id: "prog-upper-lower-4d",
      name: "4-Day Upper / Lower Split",
      daysPerWeek: 4,
      duration: "45–55 mins",
      target: "Intermediate / Lean Muscle Growth & Progressive Overload",
      location: "Gym or Well-Equipped Home",
      desc: "Splits workouts into Upper body and Lower body days twice a week, allowing ideal muscle recovery and weekly volume.",
      days: [
        {
          name: "Day 1: Upper Body Power",
          exercises: ["ex01", "ex05", "ex09", "ex06", "ex18", "ex19"]
        },
        {
          name: "Day 2: Lower Body & Core Focus",
          exercises: ["ex12", "ex14", "ex15", "ex16", "ex17", "ex21"]
        },
        {
          name: "Day 3: Upper Body Hypertrophy",
          exercises: ["ex02", "ex07", "ex10", "ex04", "ex11", "ex18"]
        },
        {
          name: "Day 4: Lower Body Posterior & Conditioning",
          exercises: ["ex14", "ex13", "ex16", "ex15", "ex22", "ex24"]
        }
      ]
    },
    {
      id: "prog-home-noequipment",
      name: "Living Room Bodyweight Strength (No Equipment)",
      daysPerWeek: 3,
      duration: "25–35 mins",
      target: "Beginner to Intermediate / Home Fitness & Toning",
      location: "Home",
      desc: "Zero equipment required. Uses leverage, controlled tempo, and functional bodyweight movements to build posture, tone, and cardiovascular fitness.",
      days: [
        {
          name: "Session 1: Total Body Core & Tone",
          exercises: ["ex03", "ex15", "ex16", "ex21", "ex23", "ex25"]
        },
        {
          name: "Session 2: Lower Body & Mobility",
          exercises: ["ex13", "ex15", "ex17", "ex20", "ex22", "ex24"]
        },
        {
          name: "Session 3: Upper Body & Conditioning",
          exercises: ["ex03", "ex20", "ex16", "ex21", "ex22", "ex25"]
        }
      ]
    },
    {
      id: "prog-express-15m",
      name: "15-Minute Daily Habit Express",
      daysPerWeek: 5,
      duration: "15 mins",
      target: "Busy Professionals / Daily Consistency",
      location: "Home or Office",
      desc: "Fast, energizing 15-minute circuits designed to maintain joint mobility, stimulate muscle tone, and prevent sitting fatigue.",
      days: [
        {
          name: "Routine A: Push & Legs Circuit",
          exercises: ["ex03", "ex15", "ex21"]
        },
        {
          name: "Routine B: Pull & Posterior Chain",
          exercises: ["ex16", "ex23", "ex22"]
        },
        {
          name: "Routine C: Full Body Burn",
          exercises: ["ex13", "ex03", "ex25"]
        }
      ]
    }
  ],

  // Evidence-based Meal Plan Templates (Balanced Pakistani & Healthy Meals)
  mealPlanTemplates: {
    weightLoss: [
      {
        day: 1,
        title: "Balanced Calorie Deficit (approx 1,550 kcal)",
        meals: [
          {
            name: "Breakfast",
            foods: ["2 Boiled Egg Whites + 1 Whole Egg", "1 Whole Wheat Roti (60g)", "1 Cup Green Tea (Kahwa) with mint"],
            calories: 320,
            protein: 21,
            carbs: 32,
            fat: 10,
            notes: "High satiety start. Green tea promotes mild alertness without sugar calories."
          },
          {
            name: "Mid-Day Snack",
            foods: ["1 Medium Fresh Apple", "7-8 Raw Almonds (Badam)"],
            calories: 145,
            protein: 2.5,
            carbs: 26,
            fat: 4.5,
            notes: "Healthy polyphenols and slow-digesting dietary fiber."
          },
          {
            name: "Lunch",
            foods: ["1.5 Cup Daal Chana (cooked)", "1 Whole Wheat Roti", "1 Bowl Fresh Kachumber Salad (cucumber, tomato, onion, lemon)"],
            calories: 510,
            protein: 24,
            carbs: 78,
            fat: 9,
            notes: "Plant-based protein with soluble fiber from lentils and hydrating raw vegetables."
          },
          {
            name: "Afternoon Chai Break",
            foods: ["1 Cup Unsweetened Chai with low-fat milk"],
            calories: 65,
            protein: 3.5,
            carbs: 5,
            fat: 3,
            notes: "Classic Pakistani comfort without empty refined sugars."
          },
          {
            name: "Dinner",
            foods: ["150g Grilled Chicken Tikka Breast", "1 Cup Steamed Rice or 1 Roti", "1 Cup Dahi (Low Fat Yogurt)"],
            calories: 510,
            protein: 52,
            carbs: 48,
            fat: 12,
            notes: "Lean evening protein to support muscle preservation during calorie deficit."
          }
        ]
      },
      {
        day: 2,
        title: "High-Protein Pakistani Deficit (approx 1,580 kcal)",
        meals: [
          {
            name: "Breakfast",
            foods: ["Desi Omelette (1 Egg + 2 Whites with green chilies & onion)", "1 Whole Wheat Roti", "1 Cup Peshawari Kahwa"],
            calories: 335,
            protein: 23,
            carbs: 33,
            fat: 11,
            notes: "Spice compounds in chilies add flavor with minimal energy density."
          },
          {
            name: "Mid-Day Snack",
            foods: ["1 Cup Plain Low-Fat Dahi with 1 tsp chia or flaxseeds"],
            calories: 135,
            protein: 10.5,
            carbs: 13,
            fat: 4,
            notes: "Probiotic gut support with protein."
          },
          {
            name: "Lunch",
            foods: ["1 Bowl Chicken Karahi (Home-cooked with less oil)", "1 Whole Wheat Roti", "Fresh Salad with lemon"],
            calories: 525,
            protein: 40,
            carbs: 37,
            fat: 22,
            notes: "Rich, satisfying Pakistani taste prepared with conscious vegetable oil portioning."
          },
          {
            name: "Afternoon Snack",
            foods: ["1 Medium Banana or Fresh Seasonal Fruit"],
            calories: 105,
            protein: 1.3,
            carbs: 27,
            fat: 0.3,
            notes: "Natural potassium and easily digestible pre-walk energy."
          },
          {
            name: "Dinner",
            foods: ["1.5 Cup Rajma / Lobia (Red Bean Curry)", "1 Cup Boiled Basmati Rice", "Kachumber Salad"],
            calories: 480,
            protein: 22,
            carbs: 86,
            fat: 4.5,
            notes: "Complex carbohydrates promote nighttime serotonin and calm sleep."
          }
        ]
      },
      {
        day: 3,
        title: "Clean Metabolic Vitality (approx 1,520 kcal)",
        meals: [
          {
            name: "Breakfast",
            foods: ["1 Cup Low Fat Milk with 4 tbsp Rolled Oats & 3 Chopped Dates", "1 Boiled Egg"],
            calories: 360,
            protein: 18,
            carbs: 56,
            fat: 8.5,
            notes: "Wholesome natural sweetness from Khajoor; sustained low-glycemic fuel."
          },
          {
            name: "Mid-Day Snack",
            foods: ["1 Cup Namkeen Salted Cumin Lassi"],
            calories: 130,
            protein: 7,
            carbs: 10,
            fat: 7,
            notes: "Cumin and dahi assist digestion and electrolyte balance."
          },
          {
            name: "Lunch",
            foods: ["1 Cup Aloo Palak or Palak Paneer", "1 Whole Wheat Roti", "1 Boiled Egg White"],
            calories: 440,
            protein: 20,
            carbs: 45,
            fat: 18,
            notes: "Dark leafy spinach rich in iron, folate, and carotenoids."
          },
          {
            name: "Afternoon Refreshment",
            foods: ["1 Cup Green Tea + 5 Walnuts halves"],
            calories: 85,
            protein: 2,
            carbs: 2,
            fat: 8,
            notes: "Plant omega-3 fatty acids for cognitive focus."
          },
          {
            name: "Dinner",
            foods: ["1 Pan-Seared Fish Fillet (Rahu/Pomfret)", "1 Cup Steamed Brown or White Rice", "Green Salad with lemon"],
            calories: 505,
            protein: 38,
            carbs: 48,
            fat: 12,
            notes: "Lean white fish provides easily assimilated amino acids."
          }
        ]
      }
    ],
    muscleGain: [
      {
        day: 1,
        title: "Clean Muscle Hypertrophy (approx 2,450 kcal)",
        meals: [
          {
            name: "Breakfast",
            foods: ["3 Whole Eggs Scrambled", "2 Whole Wheat Rotis or 1 Light Paratha", "1 Glass Milk (250ml) with 3 Dates"],
            calories: 680,
            protein: 38,
            carbs: 72,
            fat: 26,
            notes: "Dense amino acid matrix with slow carbs to fuel morning training."
          },
          {
            name: "Mid-Morning Fuel",
            foods: ["1 Cup Whole Milk Dahi with 1 Banana & 15 Almonds"],
            calories: 360,
            protein: 14,
            carbs: 44,
            fat: 15,
            notes: "Clean calorie surplus rich in calcium, potassium and monounsaturated fats."
          },
          {
            name: "Lunch",
            foods: ["200g Chicken Karahi", "1.5 Cup Basmati Rice", "1 Bowl Daal Masoor", "Fresh Salad"],
            calories: 740,
            protein: 52,
            carbs: 85,
            fat: 22,
            notes: "High leucine concentration for maximal muscle protein synthesis."
          },
          {
            name: "Pre-Workout / Afternoon",
            foods: ["1 Cup Unsweetened Chai + 2 Shami Kababs (Chicken or Beef)"],
            calories: 310,
            protein: 28,
            carbs: 18,
            fat: 12,
            notes: "Pre-session protein and mild caffeine boost."
          },
          {
            name: "Dinner",
            foods: ["1 Bowl Beef or Chicken Haleem (300g)", "1 Tandoori Naan or 2 Rotis", "Cucumber Raita"],
            calories: 780,
            protein: 48,
            carbs: 98,
            fat: 20,
            notes: "High carbohydrate replenishment to restore glycogen post-training."
          }
        ]
      }
    ],
    maintenance: [
      {
        day: 1,
        title: "Healthy Daily Maintenance (approx 1,950 kcal)",
        meals: [
          {
            name: "Breakfast",
            foods: ["2 Eggs Desi Omelette", "1 Whole Wheat Roti", "1 Cup Chai with light milk"],
            calories: 420,
            protein: 20,
            carbs: 38,
            fat: 19,
            notes: "Balanced Pakistani breakfast that keeps energy level stable till lunch."
          },
          {
            name: "Mid-Morning",
            foods: ["1 Cup Fresh Seasonal Fruit (Apple or Mango in season) + 10 Almonds"],
            calories: 180,
            protein: 4,
            carbs: 28,
            fat: 7,
            notes: "Antioxidant and micronutrient boost."
          },
          {
            name: "Lunch",
            foods: ["1 Cup Chicken Salan or Daal Chana", "1.5 Rotis", "1 Bowl Kachumber Salad", "0.5 Cup Dahi"],
            calories: 580,
            protein: 34,
            carbs: 68,
            fat: 18,
            notes: "Nutritionally balanced home-cooked lunch."
          },
          {
            name: "Afternoon Snack",
            foods: ["1 Cup Green Tea (Kahwa) with mint + small handful roasted peanuts"],
            calories: 130,
            protein: 5,
            carbs: 5,
            fat: 10,
            notes: "Satisfying healthy crunch."
          },
          {
            name: "Dinner",
            foods: ["150g Grilled Fish or Chicken Tikka", "1 Cup Basmati Rice", "Mixed Steamed/Stir-fried Sabzi"],
            calories: 560,
            protein: 44,
            carbs: 60,
            fat: 14,
            notes: "Clean evening meal promoting restful recovery."
          }
        ]
      }
    ]
  },

  // 20 In-Depth SEO Educational Articles with FAQs & Structured Data
  seoArticles: [
    {
      slug: "calorie-calculator",
      title: "Calorie Calculator: Estimate Daily Needs for Weight & Health",
      h1: "Daily Calorie Calculator: Evidence-Based Energy Balance",
      metaDesc: "Calculate your daily calorie needs for healthy weight loss, maintenance, or muscle gain using the Mifflin-St Jeor formula and local Pakistani food data.",
      category: "Calculators",
      readTime: "5 min read",
      lead: "Understanding your daily energy expenditure is the foundation of sustainable health. Discover how your body burns energy and calculate an evidence-based calorie target without extreme restriction.",
      calcType: "calorie",
      content: `
        <h3>What Are Calories and Energy Balance?</h3>
        <p>A calorie is simply a unit of energy. Your body requires continuous energy to sustain basic cellular functions, breathe, circulate blood, digest nutrients, and perform physical activity. The principle of energy balance states that when calorie intake matches energy expenditure over time, body weight remains stable.</p>
        
        <h3>The Mifflin-St Jeor Equation</h3>
        <p>This calculator employs the peer-reviewed Mifflin-St Jeor equation, widely recognized by dietetic associations as the most reliable non-clinical estimation tool for adults:</p>
        <ul>
          <li><strong>For Men:</strong> BMR = (10 × weight in kg) + (6.25 × height in cm) - (5 × age) + 5</li>
          <li><strong>For Women:</strong> BMR = (10 × weight in kg) + (6.25 × height in cm) - (5 × age) - 161</li>
        </ul>
        <p>Your BMR is then adjusted by an Activity Multiplier (ranging from 1.2 for sedentary desk routines up to 1.9 for rigorous daily athletic training) to estimate your Total Daily Energy Expenditure (TDEE).</p>

        <h3>Responsible Calorie Adjustments</h3>
        <p>Healthy weight change is gradual. For weight loss, a moderate deficit of 300 to 500 kcal per day typically corresponds to a steady, sustainable change of approximately 0.25 to 0.5 kg per week. For healthy weight gain and muscle building, a modest surplus of 250 to 400 kcal per day combined with resistance training promotes lean mass development rather than excessive fat accumulation.</p>
      `,
      faqs: [
        {
          q: "Is a calorie calculator 100% accurate?",
          a: "No mathematical formula can measure individual metabolism with absolute precision. Factors like non-exercise activity, genetics, and hormone levels cause variations of ±10%. Treat the result as an educated baseline to monitor and adjust over 2–3 weeks."
        },
        {
          q: "What is the absolute minimum calorie target for safety?",
          a: "General clinical guidelines advise against unmonitored intakes below 1,200 calories daily for adult females and 1,500 calories daily for adult males to avoid nutrient deficiencies and metabolic slowdown."
        }
      ]
    },
    {
      slug: "bmi-calculator",
      title: "BMI Calculator: Body Mass Index & Healthy Weight Screening",
      h1: "Body Mass Index (BMI) Screening & Healthy Range Guide",
      metaDesc: "Check your adult Body Mass Index (BMI), learn the standard WHO and South Asian thresholds, and understand healthy body composition screening.",
      category: "Calculators",
      readTime: "4 min read",
      lead: "Body Mass Index (BMI) is a universal screening tool comparing weight against height. Learn what your BMI category signifies and its clinical limitations.",
      calcType: "bmi",
      content: `
        <h3>How BMI is Calculated</h3>
        <p>Body Mass Index is derived using the standard formula: <code>BMI = weight (kg) / [height (m)]²</code>. For imperial measurements, it is: <code>BMI = [weight (lbs) × 703] / [height (in)]²</code>.</p>

        <h3>Standard Adult Classifications (WHO)</h3>
        <ul>
          <li><strong>Underweight:</strong> BMI below 18.5</li>
          <li><strong>Healthy / Normal Weight:</strong> BMI 18.5 – 24.9</li>
          <li><strong>Overweight:</strong> BMI 25.0 – 29.9</li>
          <li><strong>Obesity (Class I):</strong> BMI 30.0 – 34.9</li>
          <li><strong>Obesity (Class II & III):</strong> BMI 35.0 and above</li>
        </ul>

        <h3>Important Clinical Limitations</h3>
        <p><strong>BMI is a screening measure and does not diagnose health conditions.</strong> BMI cannot distinguish between skeletal muscle mass, bone density, fluid retention, and adipose tissue. An athletic individual with high muscularity may register as 'overweight' despite having very low body fat. Always evaluate BMI alongside waist circumference, physical fitness, blood markers, and dietary quality.</p>
      `,
      faqs: [
        {
          q: "Does South Asian ethnicity affect BMI interpretations?",
          a: "Yes. Health organizations note that South Asian populations frequently carry a higher proportion of visceral abdominal fat at lower BMI values, prompting some public health guidelines to suggest an optimal BMI threshold between 18.5 and 23 for South Asian adults."
        },
        {
          q: "Can I use BMI during pregnancy?",
          a: "No. BMI formulas are not designed for pregnant or breastfeeding individuals, nor are they calibrated for growing children or competitive strength athletes."
        }
      ]
    },
    {
      slug: "pakistani-food-calorie-calculator",
      title: "Pakistani Food Calorie Calculator: Roti, Biryani & Traditional Meals",
      h1: "Pakistani Food Calorie & Macro Guide: Traditional Cuisine Explained",
      metaDesc: "Discover estimated calories, protein, carbohydrates, and fats in roti, biryani, chicken karahi, daal, and traditional Pakistani dishes.",
      category: "Nutrition",
      readTime: "6 min read",
      lead: "Enjoying your traditional Pakistani cuisine while achieving your fitness targets is completely realistic when you understand portion sizes and cooking methods.",
      calcType: "food",
      content: `
        <h3>Traditional Pakistani Cooking and Calorie Variations</h3>
        <p>Traditional Desi meals are naturally nutrient-rich, combining wholesome whole-wheat rotis, antioxidant-packed spices like turmeric and cumin, protein-rich lentils (daals), and fresh salads (kachumber). However, total calories can vary dramatically depending on the amount of cooking oil, ghee, and frying employed.</p>
        
        <h3>Cooking Smarter Without Sacrificing Flavor</h3>
        <ul>
          <li><strong>Measure Cooking Oil:</strong> Traditional curries often use 4 to 6 tablespoons of oil. Reducing this to 1.5 to 2 tablespoons per family handi saves hundreds of hidden calories without compromising taste.</li>
          <li><strong>Choose Tawa over Tandoori Roghani:</strong> A standard home whole-wheat chapati contains ~150 kcal, whereas a restaurant Roghani Naan brushed with ghee can exceed 400 kcal.</li>
          <li><strong>Lean Protein Sources:</strong> Opt for skinless chicken breast tikka, pan-cooked spiced fish, or egg whites to elevate protein without saturated fats.</li>
          <li><strong>Vegetable Volume:</strong> Add generous kachumber salad (cucumber, tomato, lemon) to your plate to increase satiety with negligible calories.</li>
        </ul>
      `,
      faqs: [
        {
          q: "How many calories are in 1 home-made roti?",
          a: "A typical 60g whole wheat chapati cooked on a tawa without oil contains approximately 140 to 155 calories, 4 to 5 grams of protein, and 30 grams of complex carbohydrates."
        },
        {
          q: "Can I eat Chicken Karahi and still lose weight?",
          a: "Yes. When prepared at home with measured vegetable oil or olive oil, skinless chicken, fresh tomatoes, and ginger, Chicken Karahi is an excellent high-protein meal."
        }
      ]
    },
    {
      slug: "weight-loss-calculator",
      title: "Weight Loss Calorie Calculator: Safe & Sustainable Targets",
      h1: "Weight Loss Planner: Realistic Timelines & Calorie Deficits",
      metaDesc: "Plan your healthy weight loss journey with a safe, moderate calorie deficit. Calculate your weekly pace, protein targets, and estimated timeline.",
      category: "Calculators",
      readTime: "5 min read",
      lead: "Rapid weight loss diets often result in rebound weight gain and muscle loss. Learn how a gentle, evidence-based deficit protects your metabolism and energy.",
      calcType: "weight-loss",
      content: `
        <h3>The Mathematics of Sustainable Weight Loss</h3>
        <p>One kilogram of body fat stores approximately 7,700 kcal of energy. Creating an average daily deficit of 500 kcal produces an estimated deficit of 3,500 kcal per week, translating to roughly 0.45 kg (1 lb) of fat loss per week. This moderate pace is clinically proven to protect thyroid function, lean muscle tissue, and psychological well-being.</p>

        <h3>Why Protein is Essential in a Calorie Deficit</h3>
        <p>Consuming adequate protein (1.6 to 2.2g per kg of body weight) signals your body to retain metabolically active muscle tissue while losing fat. Furthermore, protein possesses a high Thermic Effect of Food (TEF), burning 20–30% of its caloric value simply through digestion, while providing superior satiety.</p>
      `,
      faqs: [
        {
          q: "Why did my weight fluctuate by 1 kg overnight?",
          a: "Daily scale fluctuations are almost always shifts in body water, glycogen storage, and digestive transit time—not body fat. High sodium meals (like restaurant curries) or heavy workouts temporarily increase water retention."
        },
        {
          q: "How often should I weigh myself?",
          a: "Weighing 2 to 3 times weekly first thing in the morning and calculating a rolling weekly average provides the clearest picture of genuine progress."
        }
      ]
    },
    {
      slug: "weight-gain-calculator",
      title: "Healthy Weight Gain Calculator: Build Muscle & Vitality",
      h1: "Healthy Weight Gain: Build Lean Mass Without Excessive Fat",
      metaDesc: "Calculate your calorie surplus and protein requirements for healthy, gradual weight gain. Perfect for active individuals and naturally lean adults.",
      category: "Calculators",
      readTime: "5 min read",
      lead: "Gaining weight in a healthy manner requires nourishing calorie density and progressive resistance training, not junk food overeating.",
      calcType: "weight-gain",
      content: `
        <h3>The Healthy Bulking Approach</h3>
        <p>For naturally lean individuals or those recovering from illness, gaining weight can be challenging. The goal is lean mass accretion rather than rapid visceral fat storage. We recommend a conservative surplus of 300 to 500 kcal above maintenance, targeting 0.25 to 0.4 kg of weight gain per week.</p>

        <h3>Nutrient-Dense Foods for Easy Calorie Intake</h3>
        <ul>
          <li><strong>Nuts and Nut Butters:</strong> Almonds, walnuts, and peanuts provide concentrated calories, healthy fats, and minerals.</li>
          <li><strong>Whole Milk & Dahi:</strong> Buffalo milk, homemade lassi, and full-fat yogurt provide easy liquid calories and high-quality protein.</li>
          <li><strong>Dried Fruits:</strong> Khajoor (dates) and raisins offer natural sweetness and energy without digestive heaviness.</li>
          <li><strong>Complex Starches:</strong> Potatoes (aloo), sweet potatoes, chickpeas, and fragrant basmati rice allow comfortable meal volume.</li>
        </ul>
      `,
      faqs: [
        {
          q: "Will all weight gain go to my belly?",
          a: "If you consume a moderate calorie surplus combined with progressive strength training (lifting weights 3–4 days per week), the surplus will primarily support muscle repair and glycogen storage rather than isolated abdominal fat."
        }
      ]
    },
    {
      slug: "macro-calculator",
      title: "Macro Calculator: Protein, Carb & Fat Balance for Your Goals",
      h1: "Macro Calculator: Optimize Your Daily Macronutrient Split",
      metaDesc: "Calculate your optimal ratio of proteins, carbohydrates, and fats tailored to your personal fitness goal, activity level, and lifestyle.",
      category: "Calculators",
      readTime: "5 min read",
      lead: "While total calories dictate weight changes, your macronutrient composition determines how your body looks, recovers, and performs.",
      calcType: "macro",
      content: `
        <h3>Understanding the Three Macronutrients</h3>
        <ul>
          <li><strong>Protein (4 kcal/g):</strong> The structural building blocks for muscle repair, enzymes, skin collagen, and immune function. Aim for 25–35% of total intake.</li>
          <li><strong>Carbohydrates (4 kcal/g):</strong> The preferred fuel source for your brain and muscular exertion during strength training. Aim for 40–55% of total intake.</li>
          <li><strong>Fats (9 kcal/g):</strong> Critical for hormone synthesis (testosterone, estrogen), cellular membrane health, and absorption of fat-soluble vitamins (A, D, E, K). Aim for 20–30% of total intake.</li>
        </ul>
      `,
      faqs: [
        {
          q: "Are low-carb diets mandatory for fat loss?",
          a: "No. Scientific studies show that when calories and protein are matched, low-carb and moderate-carb diets produce comparable fat loss. Adherence and personal food preferences are the key determinants of success."
        }
      ]
    },
    {
      slug: "protein-calculator",
      title: "Protein Intake Calculator: Daily Grams for Adults & Athletes",
      h1: "Daily Protein Calculator: Tailor Intake to Your Body Weight",
      metaDesc: "Calculate your optimal daily protein target in grams based on body weight, age, and activity level. Learn the best local Pakistani protein sources.",
      category: "Calculators",
      readTime: "4 min read",
      lead: "Protein is the most satiating macronutrient and the primary driver of lean tissue synthesis. Determine your exact daily requirement.",
      calcType: "protein",
      content: `
        <h3>Evidence-Based Protein Guidelines</h3>
        <p>While the basic Recommended Dietary Allowance (RDA) is 0.8g per kilogram of body weight to prevent deficiency in sedentary adults, modern sports nutrition guidelines recommend significantly higher amounts for active adults and those in a calorie deficit:</p>
        <ul>
          <li><strong>Sedentary Adults:</strong> 1.0 – 1.2g per kg</li>
          <li><strong>Active & Endurance Training:</strong> 1.4 – 1.7g per kg</li>
          <li><strong>Strength Training & Hypertrophy:</strong> 1.6 – 2.2g per kg</li>
          <li><strong>Weight Loss Deficit (Muscle Retention):</strong> 1.8 – 2.4g per kg of lean mass</li>
        </ul>
      `,
      faqs: [
        {
          q: "What are the richest Pakistani protein sources?",
          a: "Boneless chicken breast, boiled eggs, fish, lean beef shami kababs, daal chana, white chickpeas, paneer, and dahi are all rich South Asian protein sources."
        }
      ]
    },
    {
      slug: "water-intake-calculator",
      title: "Daily Water Intake Calculator: Hydration for Health & Fitness",
      h1: "Daily Water Intake Calculator: Personalized Hydration Needs",
      metaDesc: "Estimate your daily hydration needs based on your body weight, physical activity, and warm climate factors.",
      category: "Calculators",
      readTime: "4 min read",
      lead: "Proper hydration optimizes metabolic efficiency, supports joint lubrication, aids kidney waste clearance, and prevents fatigue.",
      calcType: "water",
      content: `
        <h3>Hydration Calculations</h3>
        <p>A reliable standard baseline is 30 to 35 ml of fluid per kilogram of body weight. For active individuals or those living in warm climates (such as Pakistan's summer seasons), add an additional 500 to 1,000 ml for every 45–60 minutes of sweat-inducing exercise.</p>
        
        <h3>Checking Your Hydration Level</h3>
        <p>The simplest method to evaluate your hydration status is monitoring urine color. A pale, straw-like yellow indicates healthy hydration, while dark amber suggests a need for increased water intake.</p>
      `,
      faqs: [
        {
          q: "Does chai or kahwa count towards daily fluid intake?",
          a: "Yes. Contrary to popular belief, mild caffeine in tea does not negate the hydrating volume of water consumed, although plain water remains the gold standard."
        }
      ]
    },
    {
      slug: "home-workout-plan",
      title: "Home Workout Plan: Full Body Fitness with Zero Equipment",
      h1: "Home Workout Plan: Build Strength & Mobility in Your Living Room",
      metaDesc: "Get an evidence-based home workout routine designed for busy schedules, using bodyweight exercises and progressive variations.",
      category: "Fitness",
      readTime: "5 min read",
      lead: "You do not need a gym membership or expensive machinery to build a strong, lean, and resilient physique.",
      calcType: "workout",
      content: `
        <h3>The 5 Foundational Movement Patterns</h3>
        <p>An effective home routine balances the human body's core kinetic chains:</p>
        <ol>
          <li><strong>Push:</strong> Standard push-ups, incline push-ups against furniture, or tricep dips.</li>
          <li><strong>Pull:</strong> Inverted table rows, doorway rows, or resistance band rows.</li>
          <li><strong>Squat:</strong> Bodyweight goblet squats, pause squats, and split squats.</li>
          <li><strong>Hinge:</strong> Glute bridges, single-leg Romanian deadlifts, and hip thrusts.</li>
          <li><strong>Core/Carry:</strong> Planks, bird dogs, and dead bugs.</li>
        </ol>
      `,
      faqs: [
        {
          q: "Can I build muscle with just bodyweight exercises?",
          a: "Yes, by utilizing mechanical disadvantage (slower tempos, pause reps, single-leg variations, and shorter rest periods) to induce muscular fatigue."
        }
      ]
    },
    {
      slug: "gym-workout-plan",
      title: "Gym Workout Plan: Structured Programs for Beginners & Intermediates",
      h1: "Gym Workout Plan: Maximize Muscle, Strength & Consistency",
      metaDesc: "Explore gym routines including Full Body, Upper/Lower splits, and Push-Pull-Legs routines with optimal rest and progressive overload.",
      category: "Fitness",
      readTime: "6 min read",
      lead: "Step into the gym with total confidence and a structured roadmap. Learn how to progress your lifts safely week after week.",
      calcType: "workout",
      content: `
        <h3>Selecting Your Ideal Split</h3>
        <ul>
          <li><strong>3 Days / Week:</strong> Full Body routines (Mon/Wed/Fri) allow each muscle to be trained 3 times weekly with 48 hours recovery.</li>
          <li><strong>4 Days / Week:</strong> Upper / Lower split (Mon/Tue and Thu/Fri) provides ideal balance between training volume and joint recovery.</li>
          <li><strong>5–6 Days / Week:</strong> Push / Pull / Legs (PPL) split is ideal for advanced trainees focusing on isolated muscular hypertrophy.</li>
        </ul>
      `,
      faqs: [
        {
          q: "What is Progressive Overload?",
          a: "Progressive overload is the gradual increase of stress placed upon the musculoskeletal system (adding weight, performing an extra repetition, or improving movement control) to stimulate ongoing adaptation."
        }
      ]
    },
    {
      slug: "fitness-tracker",
      title: "Fitness & Habit Tracker: Build Sustainable Consistency",
      h1: "Fitness Progress & Consistency Tracker: Long-Term Wellness",
      metaDesc: "Track your body weight, workouts, hydration, and daily habits with privacy-first local storage analytics and interactive trend charts.",
      category: "Wellness",
      readTime: "4 min read",
      lead: "Sustainable health is not built on temporary motivation; it is built on micro-habits repeated day after day.",
      calcType: "tracker",
      content: `
        <h3>The 8 Pillars of Daily Vitality</h3>
        <p>Our wellness system focuses on actionable daily milestones: consistent hydration, 7,500+ steps, nutrient-dense nutrition, strength training, restorative sleep, and mental balance. Track these daily to build unwavering momentum.</p>
      `,
      faqs: [
        {
          q: "Where is my data stored?",
          a: "All your logs, weights, habits, and meal plans are stored strictly inside your browser's private localStorage. No personal data ever leaves your device or touches any server."
        }
      ]
    }
  ]
};

// Export globally for browser usage without module bundlers
if (typeof window !== "undefined") {
  window.PAREERO_DATA = PAREERO_DATA;
}
